/**
 * LinkedIn Sales Navigator Scraper — wraps Apify
 * `curious_coder/linkedin-sales-navigator-search-scraper`.
 * Lets the user paste a search URL + LinkedIn cookie, scrape leads,
 * filter, export CSV and promote rows into the main /leads CRM.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import KanbanTodayBadge, { notifyLeadConverted } from "../components/KanbanTodayBadge";
import { toast } from "sonner";
import {
  Linkedin, Search, Loader2, Mail, ExternalLink, UserPlus, Filter,
  RefreshCw, Download, Square, Play, FileText, KeyRound, ShieldCheck,
  Building2, Briefcase, BookOpen,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import ScraperHelp from "../components/ScraperHelp";
import { useLang } from "../i18n/LangContext";
import { useAutoRefresh, AutoRefreshToggle } from "../hooks/useAutoRefresh";
import { useTrialLock } from "../hooks/useTrialLock";
import { blockExportIfTrial } from "../components/TrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LinkedInScraper() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [tab, setTab] = useState("freshdata");
  const [stats, setStats] = useState({ total_leads: 0, leads_with_email: 0, top_companies: [], top_titles: [] });
  const [cookieOk, setCookieOk] = useState(false);

  const reloadStats = useCallback(() => axios.get(`${API}/linkedin/stats`).then((r) => setStats(r.data)).catch(() => {}), []);
  const reloadCookie = useCallback(() => axios.get(`${API}/linkedin/cookie/status`).then((r) => setCookieOk(!!r.data?.configured)).catch(() => {}), []);
  useEffect(() => { reloadStats(); reloadCookie(); }, [reloadStats, reloadCookie]);
  const auto = useAutoRefresh(reloadStats, 30000, { defaultEnabled: true });

  return (
    <div className="min-h-screen" style={{ background: "var(--al-bg)" }} data-testid="li-scraper-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col">
        <header className="flex items-center gap-3 px-4 lg:px-8 h-[70px] border-b border-[var(--al-border)] bg-[var(--al-card-bg)]">
          <Logo size={28} />
          <div className="w-9 h-9 rounded-lg bg-[#0A66C2] text-white flex items-center justify-center">
            <Linkedin className="w-5 h-5" />
          </div>
          <h1 className="text-[16px] font-bold tracking-tight">
            <span className="text-[#0A66C2]">LinkedIn Sales Navigator</span>
            <span className="text-[var(--al-text-muted)] ml-1.5 font-normal">— Scraper</span>
          </h1>
          <span className="ml-3 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">
            Apify · Real
          </span>
          {cookieOk ? (
            <span data-testid="li-cookie-ok" className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 uppercase inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> {isPt ? "Cookie OK" : "Cookie OK"}
            </span>
          ) : (
            <span data-testid="li-cookie-missing" className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700 uppercase">
              {isPt ? "Cookie pendente" : "Cookie missing"}
            </span>
          )}
          <AutoRefreshToggle {...auto} label={isPt ? "Auto-atualizar" : "Auto-refresh"} />
          <div className="ml-auto flex items-center gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-6 lg:px-10 py-6 max-w-[1320px] w-full">
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi testId="li-kpi-total" label={isPt ? "Leads coletados" : "Leads captured"} value={stats.total_leads} tone="text-[#0A66C2]" />
            <Kpi testId="li-kpi-with-email" label={isPt ? "Com e-mail" : "With email"} value={stats.leads_with_email} tone="text-emerald-600" />
            <Kpi testId="li-kpi-companies" label={isPt ? "Empresas únicas" : "Unique companies"} value={(stats.top_companies || []).length} tone="text-indigo-600" />
            <Kpi testId="li-kpi-titles" label={isPt ? "Cargos top" : "Top titles"} value={(stats.top_titles || []).length} tone="text-amber-600" />
          </div>

          <div className="flex gap-1 border-b border-[var(--al-border)] mb-5">
            {[
              { id: "freshdata", icon: Search, en: "Sales Nav (no cookie)", pt: "Sales Nav (sem cookie)" },
              { id: "scrape", icon: KeyRound, en: "Legacy (cookie)", pt: "Antigo (cookie)" },
              { id: "results", icon: Briefcase, en: "Results", pt: "Resultados" },
              { id: "runs", icon: Play, en: "Runs", pt: "Execuções" },
              { id: "cookie", icon: KeyRound, en: "Cookie", pt: "Cookie" },
              { id: "help", icon: BookOpen, en: "Guide", pt: "Guia de Uso" },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  data-testid={`li-tab-${t.id}`}
                  className={`px-4 h-10 inline-flex items-center gap-2 text-[13px] font-bold rounded-t-md ${active ? "bg-[var(--al-card-bg)] border-x border-t border-[var(--al-border)] text-[#0A66C2] -mb-px" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}>
                  <Icon className="w-3.5 h-3.5" /> {isPt ? t.pt : t.en}
                </button>
              );
            })}
          </div>

          {tab === "freshdata" && <FreshdataTab isPt={isPt} onDone={() => { reloadStats(); setTab("results"); }} />}
          {tab === "scrape" && <ScrapeTab isPt={isPt} cookieOk={cookieOk} onDone={() => { reloadStats(); setTab("results"); }} onCookieNeeded={() => setTab("cookie")} />}
          {tab === "results" && <ResultsTab isPt={isPt} stats={stats} onMutate={reloadStats} />}
          {tab === "runs" && <RunsTab isPt={isPt} onIngested={reloadStats} />}
          {tab === "cookie" && <CookieTab isPt={isPt} cookieOk={cookieOk} onSaved={reloadCookie} />}
          {tab === "help" && (
            <ScraperHelp
              title={isPt ? "Guia completo — LinkedIn Sales Navigator Scraper" : "Complete Guide — LinkedIn Sales Nav Scraper"}
              intro={isPt
                ? "Existem 2 motores: ① Sales Nav SEM COOKIE (recomendado — não precisa exportar cookie, usa o actor freshdata) e ② o motor antigo com cookie. O fluxo recomendado tem 2 passos: iniciar a busca e, 5–10 minutos depois, buscar os resultados."
                : "There are 2 engines: ① Sales Nav WITHOUT COOKIE (recommended — freshdata actor, no cookie export) and ② the legacy cookie engine. The recommended flow has 2 steps: start the search, then fetch results 5–10 minutes later."}
              steps={[
                {
                  title: isPt ? "1. Monte sua busca no Sales Navigator" : "1. Build your search in Sales Navigator",
                  body: isPt
                    ? "Abra o Sales Navigator e aplique os filtros (cargo, geografia, indústria, tamanho de empresa, empresa atual). Quando a lista de resultados aparecer, copie a URL completa da barra de endereço — ela contém todos os filtros."
                    : "Open Sales Navigator and apply filters (title, geography, industry, company size, current company). When results show, copy the full URL from the address bar — it carries all filters.",
                  link: { href: "https://www.linkedin.com/sales/search/people", label: isPt ? "Abrir busca do Sales Navigator" : "Open Sales Navigator search" },
                },
                {
                  title: isPt ? "2. Cole a URL na aba 'Sales Nav (sem cookie)' e clique em Iniciar busca" : "2. Paste the URL in 'Sales Nav (no cookie)' and click Start",
                  body: isPt
                    ? "Defina a quantidade de leads (até 500 por página). O motor devolve um ID de requisição e começa a preparar os dados em segundo plano. Não precisa de cookie nem de manter a aba aberta."
                    : "Set the lead count (up to 500 per page). The engine returns a request ID and prepares data in background. No cookie needed, no need to keep the tab open.",
                },
                {
                  title: isPt ? "3. Aguarde 5–10 minutos e clique em 'Buscar resultados'" : "3. Wait 5–10 minutes and click 'Fetch results'",
                  body: isPt
                    ? "Sua busca fica listada em '2 · Buscas iniciadas'. Clique em Buscar resultados — se ainda não estiver pronto, o app avisa; tente de novo em alguns minutos. Os perfis (nome, cargo, empresa, localização, URL do perfil) entram automaticamente na aba Resultados."
                    : "Your search is listed under '2 · Started searches'. Click Fetch results — if not ready yet the app tells you; retry in a few minutes. Profiles land automatically in the Results tab.",
                },
                {
                  title: isPt ? "4. Refine, exporte e converta em clientes" : "4. Refine, export and convert",
                  body: isPt
                    ? "Na aba Resultados, filtre por Empresa/Cargo, exporte CSV ou clique em 'Lead' para enviar direto ao CRM Kanban. Combine com o Enrichment (Hunter/Apollo) para descobrir o e-mail do decisor e dispare campanhas."
                    : "In Results, filter by Company/Title, export CSV or click 'Lead' to push into the CRM Kanban. Combine with Enrichment (Hunter/Apollo) to find the decision-maker email, then launch campaigns.",
                },
                {
                  title: isPt ? "5. (Opcional) Motor antigo com cookie" : "5. (Optional) Legacy cookie engine",
                  body: isPt
                    ? "A aba 'Antigo (cookie)' usa sua própria sessão do LinkedIn (cookie li_at exportado via extensão Cookie-Editor). Só use se precisar de campos extras — o cookie expira em ~7 dias e exige assinatura Sales Navigator ativa."
                    : "The 'Legacy (cookie)' tab uses your own LinkedIn session (li_at cookie exported via Cookie-Editor). Use only if you need extra fields — cookies expire in ~7 days and require an active Sales Navigator subscription.",
                },
              ]}
              tips={isPt ? [
                "Cookies expiram em ~7 dias — re-exporte se receber 401.",
                "Buscas maiores que 1000 leads: rode em background overnight.",
                "Combine com Hunter (CRM → Lead → Enrich Decision Maker) para conseguir o e-mail real.",
              ] : [
                "Cookies expire in ~7 days — re-export when you see 401.",
                "Searches >1000 leads: run in background overnight.",
                "Combine with Hunter (CRM → Lead → Enrich Decision Maker) to surface the real email.",
              ]}
              troubleshooting={isPt ? [
                { problem: "401 / Cookie inválido", solution: "Logue em linkedin.com/sales, faça nova export do cookie e re-cole." },
                { problem: "URL recusada", solution: "Garanta que está em linkedin.com/sales (não /search). URLs do feed normal não funcionam." },
                { problem: "Nenhum lead retornado", solution: "Filtros muito restritos. Afrouxe geografia/indústria e tente de novo." },
              ] : [
                { problem: "401 / invalid cookie", solution: "Log into linkedin.com/sales, re-export the cookie and re-paste." },
                { problem: "URL rejected", solution: "Make sure it's linkedin.com/sales (not /search). Regular feed URLs don't work." },
                { problem: "No leads returned", solution: "Filters too tight. Loosen geo/industry and retry." },
              ]}
              links={[
                { href: "https://apify.com/curious_coder/linkedin-sales-navigator-search-scraper", label: "Apify · LinkedIn Sales Nav" },
                { href: "https://www.linkedin.com/sales/", label: "Sales Navigator" },
              ]}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  return (
    <div className="al-card p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">{label}</div>
      <div className={`text-[26px] font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

// ---- Freshdata tab (Iter133 — no cookie needed) ----------------------------
function FreshdataTab({ isPt, onDone }) {
  const [salesUrl, setSalesUrl] = useState("https://www.linkedin.com/sales/search/people?query=(spellCorrectionEnabled%3Atrue%2Ckeywords%3Afounder)");
  const [limit, setLimit] = useState(25);
  const [busy, setBusy] = useState(false);
  const [fetchBusy, setFetchBusy] = useState("");
  const [requests, setRequests] = useState([]);
  const [polling, setPolling] = useState(null); // {request_id, attempt, nextIn}
  const pollRef = useRef(null);

  const loadRequests = useCallback(() => {
    axios.get(`${API}/linkedin/freshdata/requests`).then((r) => setRequests(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadRequests(); return () => clearTimeout(pollRef.current); }, [loadRequests]);

  const stopPoll = () => { clearTimeout(pollRef.current); setPolling(null); };

  // Auto-poll: tries fetch every 75s (max 12x ≈ 15 min) so results arrive
  // without the user having to come back and click manually.
  const startAutoPoll = (requestId, lim, attempt = 1) => {
    const MAX = 12, WAIT = 75;
    setPolling({ request_id: requestId, attempt, nextIn: WAIT });
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/linkedin/freshdata/fetch`,
          { request_id: requestId, limit: Number(lim), page: 1 }, { timeout: 280000 });
        if (data.ok) {
          stopPoll();
          toast.success(isPt
            ? `✅ ${data.inserted} novos leads importados automaticamente (${data.fetched} perfis)`
            : `✅ ${data.inserted} new leads auto-imported (${data.fetched} profiles)`);
          loadRequests();
          onDone?.();
          return;
        }
      } catch (_e) { /* keep polling */ }
      if (attempt >= MAX) {
        stopPoll();
        toast.info(isPt
          ? "Resultados ainda não prontos após 15 min — use 'Buscar resultados' manualmente em instantes."
          : "Results not ready after 15 min — try 'Fetch results' manually shortly.");
        return;
      }
      startAutoPoll(requestId, lim, attempt + 1);
    }, WAIT * 1000);
  };

  const init = async () => {
    if (!salesUrl.includes("linkedin.com/sales")) {
      toast.error(isPt ? "Cole uma URL do Sales Navigator (linkedin.com/sales)." : "Paste a Sales Navigator URL.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/linkedin/freshdata/init`, { sales_url: salesUrl.trim(), limit: Number(limit) }, { timeout: 280000 });
      toast.success(isPt
        ? `Busca iniciada! Os leads serão importados automaticamente em ${data.eta} — pode continuar navegando.`
        : `Search started! Leads will auto-import in ${data.eta} — feel free to keep working.`);
      loadRequests();
      startAutoPoll(data.request_id, Number(limit));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const fetchResults = async (requestId, lim) => {
    setFetchBusy(requestId);
    try {
      const { data } = await axios.post(`${API}/linkedin/freshdata/fetch`, { request_id: requestId, limit: Number(lim || limit), page: 1 }, { timeout: 280000 });
      if (data.ok) {
        toast.success(isPt ? `${data.inserted} novos leads importados (${data.fetched} perfis)` : `${data.inserted} new leads (${data.fetched} profiles)`);
        loadRequests();
        onDone?.();
      } else {
        toast.info(data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setFetchBusy(""); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="al-card p-6 space-y-4" data-testid="li-fd-form">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">{isPt ? "Recomendado · Sem cookie" : "Recommended · No cookie"}</span>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "URL da busca no Sales Navigator" : "Sales Navigator search URL"}
          </div>
          <input type="text" value={salesUrl} onChange={(e) => setSalesUrl(e.target.value)}
            data-testid="li-fd-url" className="w-full al-input h-10 text-[12.5px] font-mono" />
        </div>
        <div className="flex items-end gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
              {isPt ? "Qtd. de leads" : "Lead count"}
            </div>
            <input type="number" min={1} max={500} value={limit} onChange={(e) => setLimit(e.target.value)}
              data-testid="li-fd-limit" className="w-28 al-input h-10 text-[13px]" />
          </div>
          <button type="button" onClick={init} disabled={busy}
            data-testid="li-fd-init-btn"
            className="al-btn-primary h-10 px-5 text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {isPt ? "1 · Iniciar busca" : "1 · Start search"}
          </button>
        </div>
        <p className="text-[11px] text-[var(--al-text-muted)]">
          {isPt
            ? "O motor prepara a busca em segundo plano (5–10 min). Depois clique em “Buscar resultados” abaixo."
            : "The engine prepares the search in background (5–10 min). Then click “Fetch results” below."}
        </p>
      </div>

      {polling && (
        <div className="al-card p-4 flex items-center gap-3 border-l-4 border-[#0A66C2]" data-testid="li-fd-polling">
          <Loader2 className="w-4 h-4 animate-spin text-[#0A66C2] shrink-0" />
          <div className="text-[12px] flex-1">
            {isPt
              ? <>Importação automática ativa — tentativa <b>{polling.attempt}/12</b>. Os leads aparecem sozinhos quando prontos (≈5–10 min). Pode navegar em outras abas.</>
              : <>Auto-import running — attempt <b>{polling.attempt}/12</b>. Leads will appear automatically when ready (≈5–10 min).</>}
          </div>
          <button type="button" onClick={stopPoll} className="text-[11px] font-bold text-[var(--al-text-muted)] hover:text-rose-500" data-testid="li-fd-polling-stop">
            {isPt ? "Parar" : "Stop"}
          </button>
        </div>
      )}

      <div className="al-card p-6" data-testid="li-fd-requests">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[13px] font-bold">{isPt ? "2 · Buscas iniciadas" : "2 · Started searches"}</h3>
          <button type="button" onClick={loadRequests} className="text-[11px] font-semibold text-[#0A66C2] hover:underline" data-testid="li-fd-reload">
            {isPt ? "Atualizar" : "Refresh"}
          </button>
        </div>
        {requests.length === 0 ? (
          <p className="text-[12px] text-[var(--al-text-muted)]">{isPt ? "Nenhuma busca iniciada ainda." : "No searches started yet."}</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.request_id} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--al-border)]" data-testid={`li-fd-req-${r.request_id.slice(0, 8)}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-mono truncate">{r.request_id}</div>
                  <div className="text-[10.5px] text-[var(--al-text-muted)] truncate">
                    {r.status === "fetched"
                      ? (isPt ? `✅ ${r.inserted ?? 0} importados de ${r.profiles ?? 0} perfis` : `✅ ${r.inserted ?? 0} imported of ${r.profiles ?? 0}`)
                      : (isPt ? "⏳ aguardando resultados (5–10 min)" : "⏳ waiting for results (5–10 min)")}
                    {" · "}{(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </div>
                <button type="button" onClick={() => fetchResults(r.request_id, r.limit)}
                  disabled={fetchBusy === r.request_id}
                  data-testid={`li-fd-fetch-${r.request_id.slice(0, 8)}`}
                  className="h-8 px-3 rounded-lg bg-[#0A66C2] text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                  {fetchBusy === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {isPt ? "Buscar resultados" : "Fetch results"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Scrape tab -----------------------------------------------------------
function ScrapeTab({ isPt, cookieOk, onDone, onCookieNeeded }) {
  const [searchUrl, setSearchUrl] = useState(
    "https://www.linkedin.com/sales/search/people?query=(spellCorrectionEnabled%3Atrue%2Ckeywords%3Afounder)"
  );
  const [totalRecords, setTotalRecords] = useState(25);
  const [startPage, setStartPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const submit = async (sync) => {
    if (!cookieOk) {
      toast.error(isPt ? "Configure o cookie do LinkedIn primeiro." : "Configure your LinkedIn cookie first.");
      onCookieNeeded?.();
      return;
    }
    if (!searchUrl.includes("linkedin.com/sales")) {
      toast.error(isPt ? "Cole uma URL do Sales Navigator (linkedin.com/sales)." : "Paste a Sales Navigator URL (linkedin.com/sales).");
      return;
    }
    const payload = {
      search_url: searchUrl.trim(),
      total_records: Number(totalRecords),
      start_page: Number(startPage),
      sync,
    };
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/linkedin/scrape`, payload, { timeout: 180000 });
      if (data.mode === "sync") {
        toast.success(isPt ? `${data.inserted} novos leads (${data.emails_total} e-mails)` : `${data.inserted} new leads (${data.emails_total} emails)`);
        if (onDone) onDone();
      } else {
        toast.success(isPt ? `Run iniciada: ${data.run_id}` : `Run started: ${data.run_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="al-card p-6 space-y-5 max-w-3xl" data-testid="li-scrape-form">
      <div>
        <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "URL do Sales Navigator" : "Sales Navigator URL"}
        </div>
        <input type="text" value={searchUrl} onChange={(e) => setSearchUrl(e.target.value)}
          placeholder="https://www.linkedin.com/sales/search/people?..."
          data-testid="li-search-url"
          className="w-full al-input h-10 text-[12.5px] font-mono" />
        <p className="text-[10.5px] text-[var(--al-text-muted)] mt-1.5">
          {isPt
            ? "Abra o Sales Navigator, faça sua busca, copie a URL completa da barra de endereço."
            : "Open Sales Navigator, run your search, copy the full URL from the address bar."}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "Total de leads" : "Total records"}
          </div>
          <input type="number" min="1" max="2500" value={totalRecords}
            onChange={(e) => setTotalRecords(e.target.value)}
            data-testid="li-total-records"
            className="w-full al-input h-9 text-[12.5px] tabular-nums" />
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "Página inicial" : "Start page"}
          </div>
          <input type="number" min="1" max="100" value={startPage}
            onChange={(e) => setStartPage(e.target.value)}
            data-testid="li-start-page"
            className="w-full al-input h-9 text-[12.5px] tabular-nums" />
        </label>
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--al-border)]">
        <button type="button" onClick={() => submit(true)} disabled={busy}
          data-testid="li-scrape-sync"
          className="h-10 px-5 rounded-lg bg-[#0A66C2] hover:bg-[#08518f] text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {isPt ? "Buscar agora (sync)" : "Scrape now (sync)"}
        </button>
        <button type="button" onClick={() => submit(false)} disabled={busy}
          data-testid="li-scrape-async"
          className="h-10 px-4 rounded-lg border-2 border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2]/10 font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          <Play className="w-4 h-4" /> {isPt ? "Rodar em background" : "Run in background"}
        </button>
      </div>

      <p className="text-[11px] text-[var(--al-text-muted)] pt-2">
        {isPt
          ? "Modo sync espera até ~110s. Para muitos leads, use background — verifique status na aba Execuções."
          : "Sync waits up to ~110s. For long jobs use background — check status on the Runs tab."}
      </p>
    </div>
  );
}

// ---- Results tab ----------------------------------------------------------
function ResultsTab({ isPt, stats, onMutate }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ company: "", title: "", has_email: null });
  const navigate = useNavigate();

  const load = async () => {
    const params = {};
    if (filters.company) params.company = filters.company;
    if (filters.title) params.title = filters.title;
    if (filters.has_email !== null) params.has_email = filters.has_email;
    params.limit = 100;
    const { data } = await axios.get(`${API}/linkedin/leads`, { params });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const promote = async (li) => {
    try {
      const { data } = await axios.post(`${API}/linkedin/leads/${li.id}/convert-to-lead`);
      notifyLeadConverted();
      toast.success(
        isPt ? "Lead criado e enviado ao Kanban" : "Lead created and sent to Kanban",
        {
          description: isPt
            ? "Visível na coluna NEW do CRM Kanban."
            : "Visible in the NEW column of the CRM Kanban.",
          action: { label: isPt ? "Abrir Kanban" : "Open Kanban",
                    onClick: () => navigate(data.kanban_url || "/kanban") },
          duration: 6000,
        }
      );
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const { onTrial } = useTrialLock();
  const exportCsv = () => {
    if (blockExportIfTrial(onTrial, "export CSV")) return;
    if (items.length === 0) return;
    const header = ["full_name","title","company_name","company_website","profile_url","headline","location","emails","created_at"];
    const rows = items.map((p) => header.map((k) =>
      `"${String(Array.isArray(p[k]) ? p[k].join(";") : (p[k] ?? "")).replace(/"/g, '""').replace(/\n/g, " ")}"`).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `linkedin_leads_${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="grid lg:grid-cols-4 gap-5" data-testid="li-results">
      <aside className="al-card p-5 space-y-5 lg:col-span-1">
        <div>
          <h3 className="text-[14px] font-bold mb-2 inline-flex items-center gap-1.5"><Filter className="w-4 h-4 text-[#0A66C2]" /> {isPt ? "Filtros" : "Filters"}</h3>
          <input type="text" value={filters.company} onChange={(e) => setFilters({ ...filters, company: e.target.value })}
            placeholder={isPt ? "Empresa contém..." : "Company contains..."} data-testid="li-filter-company"
            className="w-full al-input h-9 text-[12.5px] mb-2" />
          <input type="text" value={filters.title} onChange={(e) => setFilters({ ...filters, title: e.target.value })}
            placeholder={isPt ? "Cargo contém..." : "Title contains..."} data-testid="li-filter-title"
            className="w-full al-input h-9 text-[12.5px] mb-2" />
          <div className="grid grid-cols-3 gap-1 text-[10.5px] font-bold uppercase">
            {[{v:null,l:isPt?"Todos":"All"},{v:true,l:isPt?"Com e-mail":"With email"},{v:false,l:isPt?"Sem":"No email"}].map((o) => (
              <button key={String(o.v)} type="button" onClick={() => setFilters({ ...filters, has_email: o.v })}
                data-testid={`li-filter-email-${o.v === null ? "all" : o.v ? "yes" : "no"}`}
                className={`py-1.5 rounded border ${filters.has_email === o.v ? "bg-[#0A66C2] text-white border-[#0A66C2]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#0A66C2]"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <button type="button" onClick={exportCsv} disabled={items.length === 0}
          data-testid="li-export-csv"
          className="w-full h-9 rounded-lg border border-[var(--al-border)] text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--al-row-alt)] disabled:opacity-50">
          <Download className="w-3.5 h-3.5" /> {isPt ? "Exportar CSV" : "Export CSV"}
        </button>

        <div className="pt-3 border-t border-[var(--al-border)]">
          <h4 className="text-[11px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)] mb-1.5 inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {isPt ? "Top empresas" : "Top companies"}</h4>
          <ul className="space-y-1">
            {(stats.top_companies || []).slice(0, 7).map((o) => (
              <li key={o.company}>
                <button type="button" onClick={() => setFilters({ company: o.company, title: "", has_email: null })}
                  className="w-full text-left text-[12px] text-[var(--al-text)] hover:text-[#0A66C2] truncate">
                  {o.company} <span className="text-[var(--al-text-muted)] tabular-nums">· {o.leads}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)] mb-1.5 inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {isPt ? "Top cargos" : "Top titles"}</h4>
          <ul className="space-y-1">
            {(stats.top_titles || []).slice(0, 7).map((o) => (
              <li key={o.title}>
                <button type="button" onClick={() => setFilters({ company: "", title: o.title, has_email: null })}
                  className="w-full text-left text-[12px] text-[var(--al-text)] hover:text-[#0A66C2] truncate">
                  {o.title} <span className="text-[var(--al-text-muted)] tabular-nums">· {o.leads}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <div className="lg:col-span-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-bold inline-flex items-center gap-2">
            {isPt ? "Resultados" : "Results"}
            <span className="text-[12px] font-medium text-[var(--al-text-muted)] tabular-nums">
              · {items.length} {isPt ? "perfis" : "profiles"}
            </span>
          </h3>
          <KanbanTodayBadge isPt={isPt} />
        </div>
        {items.length === 0 ? (
          <div className="al-card p-10 text-center text-[var(--al-text-muted)]">
            {isPt ? "Nenhum lead ainda. Vá em 'Coletar' e cole uma URL do Sales Navigator." : "No leads yet. Use 'Scrape' and paste a Sales Navigator URL."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((p) => (
              <div key={p.id} className="al-card p-4 flex flex-col gap-1.5">
                <a href={p.profile_url} target="_blank" rel="noopener noreferrer"
                   className="text-[14px] font-bold text-[#0A66C2] hover:underline truncate">
                  {p.full_name || "(no name)"}
                </a>
                {p.headline && <p className="text-[12px] text-[var(--al-text)] line-clamp-2 leading-snug">{p.headline}</p>}
                {p.title && (
                  <div className="text-[11.5px] text-[var(--al-text-muted)] inline-flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> {p.title}
                  </div>
                )}
                {p.company_name && (
                  <div className="text-[11.5px] text-[var(--al-text-muted)] inline-flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {p.company_name}
                  </div>
                )}
                {p.location && (
                  <div className="text-[10.5px] text-[var(--al-text-muted)]">{p.location}</div>
                )}
                {p.emails && p.emails.length > 0 && (
                  <div className="mt-1 text-[11.5px] text-emerald-600 font-bold flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3" /> {p.emails[0]}
                  </div>
                )}
                <div className="flex gap-1 mt-2 pt-2 border-t border-[var(--al-border)]">
                  <button type="button" onClick={() => promote(p)}
                    data-testid={`li-promote-${p.id}`}
                    title={isPt ? "Converter em lead" : "Convert to lead"}
                    className="flex-1 h-7 rounded text-[10.5px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center justify-center gap-1">
                    <UserPlus className="w-3 h-3" /> {isPt ? "Lead" : "Lead"}
                  </button>
                  <a href={p.profile_url} target="_blank" rel="noopener noreferrer"
                     className="h-7 px-2 rounded text-[10.5px] font-bold border border-[var(--al-border)] hover:bg-[var(--al-row-alt)] inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> LI
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Runs tab -------------------------------------------------------------
function RunsTab({ isPt, onIngested }) {
  const [runId, setRunId] = useState("");
  const [info, setInfo] = useState(null);
  const [log, setLog] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!runId) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/linkedin/runs/${runId}`);
      setInfo(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const fetchLog = async () => {
    if (!runId) return;
    const { data } = await axios.get(`${API}/linkedin/runs/${runId}/log?tail=4000`);
    setLog(data.log || "");
  };
  const ingest = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/linkedin/runs/${runId}/items`, { params: { persist: true, limit: 200 } });
      toast.success(isPt ? `${data.inserted} novos leads ingeridos` : `${data.inserted} new leads ingested`);
      if (onIngested) onIngested();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const abort = async () => {
    if (!window.confirm(isPt ? "Abortar esta execução?" : "Abort this run?")) return;
    await axios.post(`${API}/linkedin/runs/${runId}/abort`);
    refresh();
  };
  const resurrect = async () => {
    await axios.post(`${API}/linkedin/runs/${runId}/resurrect`);
    toast.success(isPt ? "Execução retomada" : "Run resurrected");
    refresh();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="li-runs">
      <div className="al-card p-5 space-y-3 lg:col-span-1">
        <h3 className="text-[14px] font-bold inline-flex items-center gap-1.5"><Play className="w-4 h-4 text-[#0A66C2]" /> {isPt ? "Inspecionar execução" : "Inspect run"}</h3>
        <input type="text" value={runId} onChange={(e) => setRunId(e.target.value)}
          placeholder="run_id..."
          data-testid="li-run-id"
          className="w-full al-input h-9 text-[12.5px]" />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={refresh} disabled={busy || !runId}
            data-testid="li-run-status"
            className="h-9 px-3 rounded-lg bg-[#0A66C2] hover:bg-[#08518f] text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Status
          </button>
          <button type="button" onClick={ingest} disabled={busy || !runId}
            data-testid="li-run-ingest"
            className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            <Download className="w-3.5 h-3.5" /> {isPt ? "Ingerir" : "Ingest"}
          </button>
          <button type="button" onClick={fetchLog} disabled={!runId} className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--al-row-alt)] disabled:opacity-60">
            <FileText className="w-3.5 h-3.5" /> Log
          </button>
          <button type="button" onClick={abort} disabled={!runId} className="h-9 px-3 rounded-lg border border-rose-300 text-rose-600 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-rose-50 disabled:opacity-60">
            <Square className="w-3.5 h-3.5" /> {isPt ? "Abortar" : "Abort"}
          </button>
          <button type="button" onClick={resurrect} disabled={!runId} className="h-9 px-3 rounded-lg border border-amber-300 text-amber-600 text-[12px] font-bold col-span-2 inline-flex items-center justify-center gap-1.5 hover:bg-amber-50 disabled:opacity-60">
            <Play className="w-3.5 h-3.5" /> {isPt ? "Reanimar (resurrect)" : "Resurrect"}
          </button>
        </div>
        {info && (
          <div className="text-[11px] space-y-1 pt-3 border-t border-[var(--al-border)]">
            <div><b>Status:</b> <span className={info.status === "SUCCEEDED" ? "text-emerald-600 font-bold" : info.status === "FAILED" ? "text-rose-600 font-bold" : "text-amber-600 font-bold"}>{info.status}</span></div>
            <div><b>Started:</b> {info.started_at}</div>
            <div><b>Finished:</b> {info.finished_at || "—"}</div>
            <div><b>Dataset:</b> <code className="text-[10px]">{info.dataset_id}</code></div>
            <div><b>Usage:</b> ${info.usage_usd || 0}</div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 al-card p-5">
        <h3 className="text-[14px] font-bold mb-2">Log</h3>
        {log ? (
          <pre className="bg-[#0a0a0a] text-[#a3e635] text-[10.5px] leading-relaxed p-3 rounded max-h-[480px] overflow-auto font-mono whitespace-pre-wrap">{log}</pre>
        ) : (
          <p className="text-[12px] text-[var(--al-text-muted)] italic">{isPt ? "Clique em Log para ver." : "Click Log to view."}</p>
        )}
      </div>
    </div>
  );
}

// ---- Cookie tab -----------------------------------------------------------
function CookieTab({ isPt, cookieOk, onSaved }) {
  const [cookie, setCookie] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (cookie.trim().length < 50) {
      toast.error(isPt ? "Cookie inválido (curto demais)." : "Cookie too short.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/linkedin/cookie`, { cookie_string: cookie });
      toast.success(isPt ? `Cookie salvo (${data.length} chars)` : `Cookie saved (${data.length} chars)`);
      setCookie("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="al-card p-6 max-w-3xl space-y-4" data-testid="li-cookie-form">
      <div>
        <h3 className="text-[15px] font-bold inline-flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#0A66C2]" />
          {isPt ? "Cookie do LinkedIn (Sales Navigator)" : "LinkedIn Cookie (Sales Navigator)"}
          {cookieOk && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">OK</span>}
        </h3>
        <p className="text-[12px] text-[var(--al-text-muted)] mt-1.5 leading-relaxed">
          {isPt
            ? "1) Instale a extensão Cookie-Editor no Chrome. 2) Abra linkedin.com/sales logado. 3) Clique no Cookie-Editor → Export → JSON. 4) Cole o JSON aqui."
            : "1) Install the Cookie-Editor Chrome extension. 2) Open linkedin.com/sales while logged in. 3) Click Cookie-Editor → Export → JSON. 4) Paste the JSON here."}
        </p>
      </div>

      <textarea value={cookie} onChange={(e) => setCookie(e.target.value)}
        rows={10}
        placeholder={`[{"domain":".linkedin.com","name":"li_at","value":"AQE..."},{"name":"JSESSIONID","value":"ajax:..."}]`}
        data-testid="li-cookie-input"
        className="w-full al-input p-3 text-[11px] font-mono leading-relaxed" />

      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={busy}
          data-testid="li-cookie-save"
          className="h-10 px-5 rounded-lg bg-[#0A66C2] hover:bg-[#08518f] text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {isPt ? "Salvar cookie" : "Save cookie"}
        </button>
      </div>

      <div className="text-[10.5px] text-[var(--al-text-muted)] bg-[var(--al-row-alt)] p-3 rounded leading-relaxed">
        {isPt
          ? "🔒 O cookie é armazenado criptografado no banco e usado apenas para autenticar o actor Apify. Nunca compartilhe esse cookie — quem o tiver acessa sua conta do Sales Navigator."
          : "🔒 The cookie is stored in your DB and used only to authenticate the Apify actor. Never share this cookie — anyone with it can access your Sales Navigator account."}
      </div>
    </div>
  );
}
